import React from "react";
import { useParams, Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import BookingDialog from "@/components/BookingDialog";
import SEO from "@/components/SEO";
import RoomPricingTable, { ROOM_MARKUP } from "@/components/RoomPricingTable";
import EquipmentPackagesSection from "@/components/EquipmentPackagesSection";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { Check, X, CalendarDays, Clock, ArrowLeft, Plane, MessageCircle } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("id-ID").format(Math.round(n));

const ROOM_LABELS = {
  quad:   { id: "Kamar Quad",   en: "Quad Room" },
  triple: { id: "Kamar Triple", en: "Triple Room" },
  double: { id: "Kamar Double", en: "Double Room" },
};

// Standard exclusions (things NOT included) — shown alongside inclusions
const EXCLUSIONS_ID = [
  "Pembuatan paspor dan biaya administrasi",
  "Suntik vaksin meningitis (bisa dibantu koordinasi)",
  "Kelebihan bagasi",
  "Pengeluaran pribadi (laundry, telepon, dll)",
  "Tip untuk muthawif, driver, dan pekerja hotel",
];
const EXCLUSIONS_EN = [
  "Passport processing & administration fees",
  "Meningitis vaccine (coordination available)",
  "Excess baggage",
  "Personal expenses (laundry, phone, etc.)",
  "Tips for muthawif, driver, and hotel staff",
];

export default function PackageDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [pkg, setPkg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selectedRoom, setSelectedRoom] = React.useState("quad");

  React.useEffect(() => {
    setLoading(true);
    api.get(`/packages/${slug}`).then((r) => setPkg(r.data)).catch(() => setPkg(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-32 pb-32 container-x text-muted-foreground">Loading…</div>;
  if (!pkg) {
    return (
      <div className="pt-32 pb-32 container-x">
        <p className="text-muted-foreground">Package not found.</p>
        <Link to="/packages" className="btn-outline mt-6">{t("common.backToPackages")}</Link>
      </div>
    );
  }

  const name = lang === "id" ? pkg.name_id : pkg.name_en;
  const description = lang === "id" ? pkg.description_id : pkg.description_en;
  const highlights = lang === "id" ? pkg.highlights_id : pkg.highlights_en;
  const inclusions = lang === "id" ? pkg.inclusions_id : pkg.inclusions_en;
  const exclusions = lang === "id" ? EXCLUSIONS_ID : EXCLUSIONS_EN;

  const basePrice = pkg.price_idr || 0;
  const selectedPrice = basePrice + (ROOM_MARKUP[selectedRoom]?.addIdr || 0);
  const roomLabel = ROOM_LABELS[selectedRoom][lang] || ROOM_LABELS[selectedRoom].id;

  const isPoster = typeof pkg.image === "string" && (
    pkg.image.includes("customer-assets") ||
    pkg.image.includes("/api/static/posters/") ||
    /h=1[2-9]\d\d|h=[2-9]\d{3}/.test(pkg.image)
  );

  return (
    <div data-testid="package-detail-page" className="pt-32 lg:pt-40 pb-32 md:pb-32">
      <SEO
        title={name}
        titleEn={pkg.name_en}
        description={pkg.description_id}
        descriptionEn={pkg.description_en}
        image={pkg.image}
        type="product"
      />
      <div className="container-x">
        <Link to="/packages" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm mb-10" data-testid="back-to-packages">
          <ArrowLeft className="h-4 w-4" /> {t("common.backToPackages")}
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {isPoster ? (
              <div className="rounded-lg overflow-hidden border border-border">
                <img src={pkg.image} alt={name} className="w-full h-auto block" />
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden border border-border aspect-[4/3]">
                <img src={pkg.image} alt={name} className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 bg-accent text-primary px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.2em]">
                {pkg.category === "umrah" ? t("packages.umrah") : t("packages.haji")}
              </span>
              {pkg.tier && (
                <span className="inline-flex items-center bg-primary text-primary-foreground px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                  {pkg.tier}
                </span>
              )}
              {isPoster && (
                <span className="inline-flex items-center bg-emerald-600 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                  ✓ Confirmed
                </span>
              )}
              {pkg.full_booked && (
                <span className="inline-flex items-center bg-red-600 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                  Full Seat
                </span>
              )}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{name}</h1>
            <p className="mt-5 text-muted-foreground leading-relaxed">{description}</p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border border-border rounded-lg p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("common.duration")}</p>
                <p className="font-serif text-lg text-primary mt-2 flex items-center gap-2"><Clock className="h-4 w-4 text-accent" />{pkg.duration}</p>
              </div>
              <div className="border border-border rounded-lg p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("common.departure")}</p>
                <p className="font-serif text-lg text-primary mt-2 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-accent" />{pkg.departure || "—"}</p>
              </div>
              {pkg.airline && (
                <div className="border border-border rounded-lg p-5 col-span-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{lang === "id" ? "Maskapai" : "Airline"}</p>
                  <p className="font-serif text-lg text-primary mt-2 flex items-center gap-2">
                    <Plane className="h-4 w-4 text-accent" />
                    {pkg.airline}{pkg.airport ? ` · ${pkg.airport}` : ""}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 p-6 bg-muted/60 rounded-lg border border-border" data-testid="detail-price-card">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {roomLabel} · {t("common.from") || "Mulai"}
              </p>
              <p className="font-serif text-3xl text-primary mt-1" data-testid="detail-selected-price">
                {selectedPrice > 0 ? `Rp ${fmt(selectedPrice)}` : (pkg.full_booked ? t("Full Seat", "Full Seat") : t("Hubungi Kami", "Contact Us"))}
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={`/daftar/${pkg.slug}`}
                  data-testid="detail-daftar-cta"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  {lang === "id" ? "Daftar Jamaah" : "Register Pilgrim"}
                </Link>
                <BookingDialog pkg={pkg}>
                  <button data-testid="detail-book-cta" className="btn-outline">
                    {lang === "id" ? "Booking Cepat" : "Quick Book"}
                  </button>
                </BookingDialog>
                <a
                  href={`https://wa.me/6282114294101?text=${encodeURIComponent(`Halo BATIK, saya tertarik dengan ${name} — ${roomLabel}.`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="btn-outline inline-flex items-center gap-2"
                  data-testid="detail-wa-cta"
                >
                  <MessageCircle className="h-4 w-4" />
                  {t("common.whatsapp")}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Highlights & Inclusions vs Exclusions */}
        <div className="mt-20">
          <div className="grid lg:grid-cols-3 gap-10">
            <div>
              <p className="overline mb-4">{t("common.highlights")}</p>
              <h3 className="font-serif text-2xl text-primary">{t("common.highlights")}</h3>
              <ul className="mt-6 space-y-3">
                {highlights.map((h, i) => (
                  <li key={i} className="flex gap-3 items-start text-foreground/80 text-sm">
                    <Check className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="tab-inclusions">
              <p className="overline mb-4 !text-emerald-700">{t("common.inclusions")}</p>
              <h3 className="font-serif text-2xl text-primary">{lang === "id" ? "Fasilitas Termasuk" : "Included"}</h3>
              <ul className="mt-6 space-y-3">
                {inclusions.map((it, i) => (
                  <li key={i} className="flex gap-3 items-start text-foreground/80 text-sm">
                    <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="tab-exclusions">
              <p className="overline mb-4 !text-red-700">{lang === "id" ? "Tidak Termasuk" : "Excluded"}</p>
              <h3 className="font-serif text-2xl text-primary">{lang === "id" ? "Fasilitas Tidak Termasuk" : "Not Included"}</h3>
              <ul className="mt-6 space-y-3">
                {exclusions.map((it, i) => (
                  <li key={i} className="flex gap-3 items-start text-foreground/80 text-sm">
                    <X className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Room Pricing Table */}
        <RoomPricingTable priceIdr={basePrice} selectedRoom={selectedRoom} onSelectRoom={setSelectedRoom} />

        {/* Equipment Add-On Packages */}
        <EquipmentPackagesSection />
      </div>

      {/* Sticky Mobile CTA */}
      {!pkg.full_booked && basePrice > 0 && (
        <StickyMobileCTA pkg={pkg} price={selectedPrice} roomLabel={roomLabel} />
      )}
    </div>
  );
}
